import { z } from 'zod';
import { isDevelopmentModeEnabled } from '../utils/envUtils';

/**
 * Jediné schéma všech voleb, které si aplikace pamatuje lokálně pro
 * přihlášeného uživatele. Nová volba = nové pole zde, ne nový klíč
 * v `localStorage`.
 *
 * Schéma je zodovské, takže tvar hlídá TypeScript i běhová validace —
 * poškozený nebo zastaralý obsah storage se zahodí místo aby propadl do UI.
 */
export const userPreferencesSchema = z.object({
  dashboardView: z
    .object({
      view: z.enum(['standard', 'compact']).catch('standard'),
      allClients: z.boolean().catch(false),
      // Kód měny, na kterou je seznam účtů zúžený. Prázdný řetězec znamená
      // „všechny měny“ — konkrétní kódy neomezujeme výčtem, protože jsou
      // dané tím, jaké účty klient má.
      currency: z.string().catch(''),
    })
    .catch({ view: 'standard', allClients: false, currency: '' }),
});

export type UserPreferences = z.infer<typeof userPreferencesSchema>;

export const DEFAULT_USER_PREFERENCES: UserPreferences =
  userPreferencesSchema.parse({});

/**
 * Sůl zapečená do buildu. Nejde o tajemství v kryptografickém smyslu — kdo
 * otevře bundle, najde ji. Cílem je, aby z obsahu `localStorage` nešlo na
 * první pohled vyčíst, který uživatel na daném počítači pracoval
 * (defense in depth), ne aby data byla nedostupná.
 */
const SALT = import.meta.env.VITE_STORAGE_SALT ?? 'penta.storage.v1';

const KEY_PREFIX = 'penta.u';

/**
 * FNV-1a (32 bit). Záměrně nekryptografický a synchronní — `SubtleCrypto` je
 * asynchronní a volba zobrazení se musí přečíst hned při prvním renderu.
 * Pro znečitelnění identifikátoru v klíči to stačí; na ochranu tajemství by
 * to nestačilo a ani k tomu neslouží.
 */
function fnv1a(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

/** Klíč nenese ID uživatele v čitelné podobě, jen jeho osolený otisk. */
function storageKey(userId: number): string {
  return `${KEY_PREFIX}.${fnv1a(`${SALT}:${userId}`)}`;
}

/**
 * Klíčový proud odvozený ze soli a ID uživatele. Bez toho by se obsah všech
 * uživatelů maskoval týmž vzorem, takže by z porovnání dvou záznamů šlo
 * odvodit, kde se liší. Řetězením FNV-1a vznikne proud delší než sůl sama,
 * aby se vzor neopakoval po pár znacích.
 */
function deriveKeystream(userId: number, length: number): string {
  let block = fnv1a(`${SALT}:${userId}`);
  let stream = block;
  while (stream.length < length) {
    block = fnv1a(`${block}:${SALT}`);
    stream += block;
  }
  return stream;
}

/**
 * Reverzibilní znečitelnění hodnoty (XOR s odvozeným proudem + base64).
 * Nejde o šifrování a netváří se tak — brání jen tomu, aby obsah šel
 * přečíst pouhým otevřením DevTools. Kdo má bundle, přečte si to taky.
 *
 * Ve vývojovém režimu se vynechává, aby šlo do úložiště normálně vidět.
 */
function obfuscate(plain: string, userId: number): string {
  if (isDevelopmentModeEnabled()) return plain;
  const key = deriveKeystream(userId, plain.length);
  let out = '';
  for (let i = 0; i < plain.length; i++) {
    out += String.fromCharCode(plain.charCodeAt(i) ^ key.charCodeAt(i));
  }
  // `unescape(encodeURIComponent(...))` udrží diakritiku v rozsahu, který btoa zvládne
  return btoa(unescape(encodeURIComponent(out)));
}

function deobfuscate(encoded: string, userId: number): string {
  // Čitelný zápis z vývojového režimu se pozná podle otevírající závorky,
  // takže se přepnutí režimu obejde bez smazání uloženého záznamu.
  if (encoded.startsWith('{')) return encoded;
  const raw = decodeURIComponent(escape(atob(encoded)));
  const key = deriveKeystream(userId, raw.length);
  let out = '';
  for (let i = 0; i < raw.length; i++) {
    out += String.fromCharCode(raw.charCodeAt(i) ^ key.charCodeAt(i));
  }
  return out;
}

/**
 * Přečte volby uživatele. Jakýkoli problém (chybějící záznam, poškozený
 * obsah, zakázaný storage) končí výchozím nastavením — persistence voleb
 * nesmí být důvod, proč se aplikace nevykreslí.
 */
export function readUserPreferences(userId?: number): UserPreferences {
  if (userId === undefined) return DEFAULT_USER_PREFERENCES;
  try {
    const stored = localStorage.getItem(storageKey(userId));
    if (!stored) return DEFAULT_USER_PREFERENCES;
    return userPreferencesSchema.parse(JSON.parse(deobfuscate(stored, userId)));
  } catch {
    return DEFAULT_USER_PREFERENCES;
  }
}

/** Zapíše volby uživatele; při nedostupném storage tiše neuloží. */
export function writeUserPreferences(
  userId: number,
  preferences: UserPreferences,
): void {
  try {
    localStorage.setItem(
      storageKey(userId),
      obfuscate(JSON.stringify(preferences), userId),
    );
  } catch {
    // Zaplněný nebo zakázaný storage — volba platí aspoň pro tuto relaci.
  }
}
